import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const testimonials = [
  {
    name: "Priya Sharma",
    role: "MSc Data Science, University of Edinburgh",
    type: "Student",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150",
    quote: "Mindways helped me articulate my story in a way I could not have done alone. From GRE prep to SOP reviews to visa — every step was planned and executed with real rigour. I had offers from three universities and chose Edinburgh.",
    stars: 5,
  },
  {
    name: "Vikram Mehta",
    role: "Parent — BEng Computer Engineering, University of Toronto",
    type: "Parent",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150",
    quote: "As a parent, I needed someone trustworthy who would give honest guidance — not just tell us what we wanted to hear. Anirudh took the time to understand my son's profile and long-term goals. The process was transparent from day one.",
    stars: 5,
  },
  {
    name: "Arjun Bhatia",
    role: "MBA, London Business School",
    type: "Student",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150",
    quote: "The MBA guidance I received was genuinely strategic — not a template. Anirudh helped me build a compelling leadership narrative and choose schools where I had a realistic shot. The result exceeded my expectations.",
    stars: 5,
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginBottom: "60px" }}
        >
          <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Families Who Trusted Us
          </span>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: "700", color: "#0F172A", margin: "12px 0 0" }}>
            What students and parents say
          </h2>
        </motion.div>

        {/* Testimonial cards */}
        <div className="mw-grid-3" style={{ gap: "24px" }}>
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              data-testid={`testimonial-card-${i}`}
              style={{
                backgroundColor: "#fff",
                borderRadius: "14px",
                padding: "36px 32px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                flexDirection: "column",
                gap: "0",
                position: "relative",
                transition: "all 0.3s",
              }}
              whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}
            >
              {/* Type badge */}
              <div style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                backgroundColor: t.type === "Parent" ? "#FFF7E6" : "#EEF0FC",
                color: t.type === "Parent" ? "#F5A000" : "#2B2F9E",
                fontSize: "11px",
                fontWeight: "700",
                padding: "4px 10px",
                borderRadius: "20px",
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: "0.05em",
              }}>
                {t.type}
              </div>

              {/* Quote icon */}
              <Quote size={28} style={{ color: "#E2E8F0", marginBottom: "16px" }} />

              {/* Stars */}
              <div style={{ display: "flex", gap: "4px", marginBottom: "16px" }}>
                {Array(t.stars).fill(0).map((_, si) => (
                  <svg key={si} width="14" height="14" viewBox="0 0 24 24" fill="#F5A000"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                ))}
              </div>

              {/* Quote text */}
              <p style={{
                fontSize: "15px",
                lineHeight: "1.7",
                color: "#475569",
                fontFamily: "'DM Sans', sans-serif",
                margin: "0 0 24px",
                flex: 1,
                fontStyle: "italic",
              }}>
                "{t.quote}"
              </p>

              {/* Author */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", borderTop: "1px solid #F1F5F9", paddingTop: "20px" }}>
                <img
                  src={t.avatar}
                  alt={t.name}
                  style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover" }}
                />
                <div>
                  <p style={{ fontSize: "15px", fontWeight: "700", color: "#0F172A", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{t.name}</p>
                  <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginTop: "24px", fontSize: "12px", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif", fontStyle: "italic" }}
        >
          Testimonials are illustrative. Names and outcomes will be updated with verified student accounts.
        </motion.p>
      </div>
    </section>
  );
}
